import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import Icon from "@ats/src/components/shared/Icon";
import { PlatoMark } from "@ats/src/components/shared/PlatoMark";
import { FitStars, fitBand, fitTagVariant } from "./FitIndicator";
import PlatoScoreTag from "./PlatoScoreTag";
import ScoringDetail, { CriterionResult } from "./ScoringDetail";

export interface PlatoSummaryData {
  headline: string;
  roleFit: string;
  scorePct: number;
  generatedAgo: string;
  criteria: CriterionResult[];
  standoutAccomplishments: string[];
  applicableExperience?: string;
  gaps?: string;
  skills: string[];
  keySkills?: string[];
  primaryDomain?: string;
  secondaryDomain?: string;
  stale?: boolean;
}

const PlatoSummary: React.FunctionComponent<{ data: PlatoSummaryData }> = ({ data }) => {
  const band = fitBand(data.scorePct);
  const keySet = new Set((data.keySkills || []).map((s) => s.toLowerCase()));
  const domains = [data.primaryDomain, data.secondaryDomain].filter(Boolean) as string[];
  const skills = [...data.skills].sort((a, b) => Number(keySet.has(b.toLowerCase())) - Number(keySet.has(a.toLowerCase())));
  const achievements = data.standoutAccomplishments || [];

  return (
    <Styled.Root>
      <Styled.Meta>
        Generated by Plato <Styled.Sep>&middot;</Styled.Sep> {data.generatedAgo}
      </Styled.Meta>

      {data.stale && <Styled.Stale>The resume has changed since this review was generated. Regenerate to update.</Styled.Stale>}

      <Styled.Score>
        <PlatoScoreTag variant={fitTagVariant(band.key)}>{band.label}</PlatoScoreTag>
        <FitStars value={band.fill} label={band.label} size={22} gap={2} />
      </Styled.Score>

      <Styled.Headline>{data.headline}</Styled.Headline>

      {domains.length > 0 && (
        <Styled.Domains>
          {domains.map((d, i) => {
            const DomainEl = i === 0 ? Styled.PrimaryDomain : Styled.SecondaryDomain;
            return (
              <React.Fragment key={d}>
                {i > 0 && <Styled.Dot aria-hidden="true" />}
                <DomainEl>{d}</DomainEl>
              </React.Fragment>
            );
          })}
        </Styled.Domains>
      )}

      {data.roleFit && (
        <Styled.RoleFit>
          <Styled.RoleFitBar />
          <Styled.RoleFitHead>
            <PlatoMark variant="sparkle" size={16} />
            Fit for this role
          </Styled.RoleFitHead>
          <Styled.RoleFitBody>{data.roleFit}</Styled.RoleFitBody>
        </Styled.RoleFit>
      )}

      {data.criteria.length > 0 && (
        <Styled.Block>
          <ScoringDetail criteria={data.criteria} />
        </Styled.Block>
      )}

      {achievements.length > 0 && (
        <Styled.Block>
          <Styled.Eyebrow>{achievements.length === 1 ? "Notable achievement" : "Notable achievements"}</Styled.Eyebrow>
          <Styled.List>
            {achievements.map((it, i) => (
              <Styled.ListItem key={i}>
                <Styled.ListIcon><Icon name="award" /></Styled.ListIcon>
                <span>{it}</span>
              </Styled.ListItem>
            ))}
          </Styled.List>
        </Styled.Block>
      )}

      {data.applicableExperience && (
        <Styled.Block>
          <Styled.Eyebrow>Relevant experience</Styled.Eyebrow>
          <Styled.Prose>{data.applicableExperience}</Styled.Prose>
        </Styled.Block>
      )}

      {data.gaps && (
        <Styled.Block>
          <Styled.Eyebrow>Open questions</Styled.Eyebrow>
          <Styled.Prose>{data.gaps}</Styled.Prose>
        </Styled.Block>
      )}

      {skills.length > 0 && (
        <Styled.Block>
          <Styled.Eyebrow>Skills</Styled.Eyebrow>
          <Styled.Chips>
            {skills.map((sk) => {
              const ChipEl = keySet.has(sk.toLowerCase()) ? Styled.KeySkillChip : Styled.SkillChip;
              return <ChipEl key={sk}>{sk}</ChipEl>;
            })}
          </Styled.Chips>
        </Styled.Block>
      )}

      <Styled.Footer>Generated by Plato — always confirm against the resume before deciding.</Styled.Footer>
    </Styled.Root>
  );
};

export default PlatoSummary;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Root = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoSummary;
    color: ${t.dark ? t.color.gray[200] : t.color.black};
  `;
});

Styled.Meta = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoSummary_Meta;
    display: flex;
    align-items: center;
    font-size: 0.8125rem;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    ${t.mb(4)}
  `;
});

Styled.Sep = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    margin: 0 ${t.spacing[1]};
    color: ${t.dark ? t.color.gray[500] : t.color.gray[300]};
  `;
});

Styled.Stale = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoSummary_Stale;
    ${[t.text.sm, t.py(3), t.px(3), t.rounded.sm, t.mb(4)]}
    background-color: ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    color: ${t.dark ? t.color.gray[200] : t.color.gray[700]};
  `;
});

Styled.Score = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoSummary_Score;
    display: flex;
    align-items: center;
    gap: ${t.spacing[2]};
    ${t.mb(4)}
  `;
});


Styled.Headline = styled.h1((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoSummary_Headline;
    ${[t.text.h3]}
    line-height: 1.3;
    color: ${t.dark ? t.color.gray[100] : t.color.black};
    margin: 0 0 ${t.spacing[3]};
  `;
});

Styled.Domains = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoSummary_Domains;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: ${t.spacing[2]};
    ${t.mb(5)}
  `;
});

Styled.PrimaryDomain = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    ${[t.text.sm, t.text.semibold]}
    color: ${t.dark ? t.color.gray[200] : t.color.black};
    white-space: nowrap;

    &::first-letter {
      text-transform: uppercase;
    }
  `;
});

Styled.SecondaryDomain = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    ${[t.text.sm, t.text.semibold]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    white-space: nowrap;

    &::first-letter {
      text-transform: uppercase;
    }
  `;
});

Styled.Dot = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: ${t.color.gray[500]};
    flex-shrink: 0;
  `;
});

Styled.RoleFit = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoSummary_RoleFit;
    position: relative;
    ${[t.py(5), t.px(4), t.rounded.md, t.mb(6)]}
    border: 1px solid ${t.dark ? "transparent" : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[800] : t.color.white};
  `;
});

Styled.RoleFitBar = styled.span`
  label: PlatoSummary_RoleFitBar;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(120deg, #fbd7ff 10%, #ffdec1 90%);
  border-radius: 0.4375rem 0 0 0.4375rem;
`;

Styled.RoleFitHead = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    display: flex;
    align-items: center;
    gap: ${t.spacing[2]};
    ${[t.text.sm, t.text.bold, t.mb(2)]}
    color: ${t.dark ? t.color.gray[200] : t.color.black};
  `;
});

Styled.RoleFitBody = styled.p((props: any) => {
  const t: any = props.theme;
  return css`
    margin: 0;
    font-size: 0.9375rem;
    line-height: 1.6;
    color: ${t.dark ? t.color.gray[300] : t.color.black};
  `;
});

Styled.Block = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    ${[t.mt(3), t.mb(6)]}
  `;
});

Styled.Eyebrow = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoSummary_Eyebrow;
    ${[t.text.xs, t.text.bold, t.mb(2)]}
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.List = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    display: flex;
    flex-direction: column;
    gap: ${t.spacing[2]};
  `;
});

Styled.ListItem = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    display: flex;
    align-items: flex-start;
    gap: ${t.spacing[3]};
    ${t.text.sm}
    line-height: 1.5;
    color: ${t.dark ? t.color.gray[300] : t.color.black};
  `;
});

Styled.ListIcon = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    flex-shrink: 0;
    margin-top: 2px;
    color: ${t.dark ? t.color.gray[300] : t.color.black};
    svg {
      width: 16px;
      height: 16px;
    }
  `;
});

Styled.Prose = styled.p((props: any) => {
  const t: any = props.theme;
  return css`
    margin: 0;
    ${t.text.sm}
    line-height: 1.5;
    color: ${t.dark ? t.color.gray[300] : t.color.black};
  `;
});

Styled.Chips = styled.div`
  label: PlatoSummary_Chips;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

Styled.SkillChip = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    display: inline-flex;
    align-items: center;
    ${[t.px(2), t.rounded.xs]}
    height: 24px;
    ${[t.text.xs, t.text.medium]}
    border: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    color: ${t.color.gray[600]};
    background: transparent;
    white-space: nowrap;
  `;
});

Styled.KeySkillChip = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    display: inline-flex;
    align-items: center;
    ${[t.px(2), t.rounded.xs]}
    height: 24px;
    ${[t.text.xs, t.text.medium]}
    border: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    color: ${t.dark ? t.color.gray[100] : t.color.black};
    background: ${t.dark ? t.color.gray[800] : t.color.gray[100]};
    white-space: nowrap;
  `;
});

Styled.Footer = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoSummary_Footer;
    ${[t.text.xs, t.pt(3)]}
    border-top: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    color: ${t.color.gray[500]};
  `;
});
